import React, { useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { Context as AuthContext } from "context/Auth";
import { fetchBookings, type BookingInterface } from "hooks/booking";
import { BookingStatus } from "types/booking";
import { Role } from "types/user";
import {
    formatEuroAmount,
    getBookingAmount,
    getTaxCreditAmount,
    isSupportedRole,
} from "./format";
import ServiceHistoryRow from "./ServiceHistoryRow";

const SERVICE_HISTORY_STATUSES = [
    BookingStatus.PENDING,
    BookingStatus.IN_PROGRESS,
    BookingStatus.TO_VALIDATE,
    BookingStatus.CLOSED,
    BookingStatus.CANCELLED,
] as const;

const bookingListParams = (status: BookingStatus) => ({
    filter_status: status,
    order: "date",
    direction: "desc",
    per_page: 500,
});

const compareBookingsDesc = (a: BookingInterface, b: BookingInterface): number => {
    const dateCompare = b.date.localeCompare(a.date);

    if (dateCompare !== 0) {
        return dateCompare;
    }

    return b.start_time.localeCompare(a.start_time);
};

const ServiceHistoryList: React.FC = () => {
    const intl = useIntl();
    const { currentRole } = useContext(AuthContext);
    const supportedRole = isSupportedRole(currentRole) ? currentRole : null;
    const enabled = supportedRole !== null;

    const closedQuery = useQuery({
        queryKey: ["profile", "service-history", supportedRole, BookingStatus.CLOSED],
        queryFn: () => fetchBookings(bookingListParams(BookingStatus.CLOSED)),
        enabled,
    });

    const inProgressQuery = useQuery({
        queryKey: ["profile", "service-history", supportedRole, BookingStatus.IN_PROGRESS],
        queryFn: () => fetchBookings(bookingListParams(BookingStatus.IN_PROGRESS)),
        enabled,
    });

    const pendingQuery = useQuery({
        queryKey: ["profile", "service-history", supportedRole, BookingStatus.PENDING],
        queryFn: () => fetchBookings(bookingListParams(BookingStatus.PENDING)),
        enabled,
    });

    const toValidateQuery = useQuery({
        queryKey: ["profile", "service-history", supportedRole, BookingStatus.TO_VALIDATE],
        queryFn: () => fetchBookings(bookingListParams(BookingStatus.TO_VALIDATE)),
        enabled,
    });

    const cancelledQuery = useQuery({
        queryKey: ["profile", "service-history", supportedRole, BookingStatus.CANCELLED],
        queryFn: () => fetchBookings(bookingListParams(BookingStatus.CANCELLED)),
        enabled,
    });

    const bookings = useMemo(() => {
        const allowedStatuses = new Set<string>(SERVICE_HISTORY_STATUSES);

        return [
            ...(pendingQuery.data ?? []),
            ...(inProgressQuery.data ?? []),
            ...(toValidateQuery.data ?? []),
            ...(closedQuery.data ?? []),
            ...(cancelledQuery.data ?? []),
        ]
            .filter((booking) => allowedStatuses.has(booking.status))
            .sort(compareBookingsDesc);
    }, [cancelledQuery.data, closedQuery.data, inProgressQuery.data, pendingQuery.data, toValidateQuery.data]);

    const activeBookings = useMemo(
        () => bookings.filter((booking) => booking.status !== BookingStatus.CANCELLED),
        [bookings],
    );

    const clientTaxCreditTotal = useMemo(
        () => supportedRole === Role.CLIENT
            ? activeBookings.reduce((sum, booking) => sum + getTaxCreditAmount(booking), 0)
            : 0,
        [activeBookings, supportedRole],
    );

    const providerTotal = useMemo(
        () => supportedRole === Role.PROVIDER
            ? activeBookings.reduce((sum, booking) => sum + getBookingAmount(booking, Role.PROVIDER), 0)
            : 0,
        [activeBookings, supportedRole],
    );
    const isLoading = enabled && (
        pendingQuery.isLoading ||
        inProgressQuery.isLoading ||
        toValidateQuery.isLoading ||
        closedQuery.isLoading ||
        cancelledQuery.isLoading
    );
    const isError = enabled && (
        pendingQuery.isError ||
        inProgressQuery.isError ||
        toValidateQuery.isError ||
        closedQuery.isError ||
        cancelledQuery.isError
    );
    const isEmpty = !isLoading && !isError && bookings.length === 0;

    return (
        <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">
                        {intl.formatMessage({ id: "profile.service_history.title" })}
                    </h2>
                    <p className="text-sm text-slate-500">
                        {intl.formatMessage({ id: "profile.service_history.subtitle" })}
                    </p>
                </div>
                {supportedRole === Role.CLIENT ? (
                    <div className="text-right">
                        <div className="text-2xl font-extrabold tabular-nums text-emerald-700">
                            {formatEuroAmount(clientTaxCreditTotal)}
                        </div>
                        <div className="text-xs font-medium text-slate-500">
                            {intl.formatMessage({ id: "profile.service_history.total_tax_credit" })}
                        </div>
                    </div>
                ) : (
                    <div className="text-right">
                        <div className="text-2xl font-extrabold tabular-nums text-emerald-700">
                            {formatEuroAmount(providerTotal)}
                        </div>
                        <div className="text-xs font-medium text-slate-500">
                            {intl.formatMessage({ id: "profile.service_history.total_earned" })}
                        </div>
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                    {intl.formatMessage({ id: "profile.service_history.loading" })}
                </div>
            ) : null}

            {isError ? (
                <div className="rounded-xl bg-red-50 p-4 text-sm font-medium text-destructive">
                    {intl.formatMessage({ id: "profile.service_history.error" })}
                </div>
            ) : null}

            {isEmpty ? (
                <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                    {intl.formatMessage({ id: "profile.service_history.empty" })}
                </div>
            ) : null}

            {!isLoading && !isError && supportedRole && bookings.length > 0 ? (
                <div className="flex flex-col gap-3">
                    {bookings.map((booking) => (
                        <ServiceHistoryRow key={booking.id} booking={booking} currentRole={supportedRole} />
                    ))}
                </div>
            ) : null}
        </section>
    );
};

export default ServiceHistoryList;
